import { useEffect, useState } from "react";

export interface AnswerFeedbackProps {
  /** Whether the answer was correct */
  isCorrect: boolean;
  /** Number of attempts made (1-based) */
  attempts: number;
  /** Stars earned (1-3), only relevant when correct */
  starsEarned?: 1 | 2 | 3;
  /** Whether to show the feedback */
  isVisible: boolean;
  /** Called when feedback animation completes */
  onComplete?: () => void;
  /** Duration to show feedback in ms (default: 2000 for correct, 1500 for incorrect) */
  duration?: number;
}

/**
 * Feedback messages based on stars earned
 */
const CORRECT_MESSAGES: Record<1 | 2 | 3, { title: string; subtitle: string }> = {
  3: { title: "Amazing!", subtitle: "Perfect on your first try!" },
  2: { title: "Great job!", subtitle: "You're doing wonderfully!" },
  1: { title: "You did it!", subtitle: "Keep up the great work!" },
};

/**
 * Encouraging messages for incorrect answers
 */
const ENCOURAGE_MESSAGES = [
  { title: "Try again!", subtitle: "You've got this!" },
  { title: "Almost there!", subtitle: "Give it another go!" },
  { title: "Keep trying!", subtitle: "You can do it!" },
  { title: "So close!", subtitle: "Try one more time!" },
];

/**
 * AnswerFeedback - Celebratory feedback overlay for challenge answers
 *
 * Shows positive, encouraging feedback after answer submission:
 * - Correct: Stars celebration with encouraging message
 * - Incorrect: Gentle encouragement to try again (never punishing)
 */
export function AnswerFeedback({
  isCorrect,
  attempts,
  starsEarned = 1,
  isVisible,
  onComplete,
  duration,
}: AnswerFeedbackProps) {
  const [showStars, setShowStars] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  // Get random encourage message for incorrect (with fallback)
  const [encourageMessage] = useState(() => {
    const randomIndex = Math.floor(Math.random() * ENCOURAGE_MESSAGES.length);
    return ENCOURAGE_MESSAGES[randomIndex] ?? ENCOURAGE_MESSAGES[0] ?? { title: "Try again!", subtitle: "You've got this!" };
  });

  const message = isCorrect ? CORRECT_MESSAGES[starsEarned] : encourageMessage;
  const actualDuration = duration ?? (isCorrect ? 2000 : 1500);

  useEffect(() => {
    if (!isVisible) {
      setShowStars(false);
      setAnimateOut(false);
      return;
    }

    // Stagger star animations for correct answers
    if (isCorrect) {
      const starTimer = setTimeout(() => setShowStars(true), 300);
      return () => clearTimeout(starTimer);
    }
  }, [isVisible, isCorrect]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate out before calling onComplete
    const animateOutTimer = setTimeout(() => {
      setAnimateOut(true);
    }, actualDuration - 300);

    const completeTimer = setTimeout(() => {
      onComplete?.();
    }, actualDuration);

    return () => {
      clearTimeout(animateOutTimer);
      clearTimeout(completeTimer);
    };
  }, [isVisible, actualDuration, onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center
        transition-opacity duration-300
        ${animateOut ? "opacity-0" : "opacity-100"}
      `}
    >
      {/* Backdrop */}
      <div
        className={`
          absolute inset-0
          ${isCorrect
            ? "bg-gradient-to-b from-meadow-100/95 to-meadow-200/95"
            : "bg-gradient-to-b from-encourage-light/95 to-sunshine-100/95"
          }
          backdrop-blur-sm
        `}
      />

      {/* Floating decorative elements */}
      {isCorrect && (
        <>
          {/* Sparkle particles */}
          {Array.from({ length: 12 }).map((_, i) => (
            <Sparkle
              key={i}
              delay={i * 100}
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
              }}
            />
          ))}
        </>
      )}

      {/* Main feedback content */}
      <div
        className={`
          relative z-10 flex flex-col items-center gap-6 p-8
          animate-celebrate
        `}
      >
        {/* Icon */}
        <div
          className={`
            w-32 h-32 rounded-full flex items-center justify-center
            shadow-playful-lg
            ${isCorrect
              ? "bg-gradient-to-br from-meadow-300 to-meadow-500"
              : "bg-gradient-to-br from-sunshine-300 to-sunshine-500"
            }
          `}
        >
          {isCorrect ? (
            <CheckmarkIcon className="w-16 h-16 text-white drop-shadow-md" />
          ) : (
            <TryAgainIcon className="w-16 h-16 text-white drop-shadow-md animate-wiggle" />
          )}
        </div>

        {/* Message */}
        <div className="text-center">
          <h2
            className={`
              font-display font-extrabold text-display-lg
              ${isCorrect ? "text-meadow-700" : "text-sunshine-700"}
              drop-shadow-sm
            `}
          >
            {message.title}
          </h2>
          <p className="font-body font-semibold text-body-xl text-neutral-700 mt-2">
            {message.subtitle}
          </p>
        </div>

        {/* Stars (only for correct answers) */}
        {isCorrect && (
          <div className="flex gap-4 mt-2">
            {[1, 2, 3].map((star) => (
              <Star
                key={star}
                filled={star <= starsEarned}
                show={showStars}
                delay={(star - 1) * 150}
              />
            ))}
          </div>
        )}

        {/* Attempt indicator (for incorrect) */}
        {!isCorrect && attempts > 1 && (
          <p className="font-body text-body-md text-neutral-500 mt-2">
            Attempt {attempts}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Star component with pop animation
 */
function Star({
  filled,
  show,
  delay,
}: {
  filled: boolean;
  show: boolean;
  delay: number;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show, delay]);

  return (
    <div
      className={`
        transition-all duration-500 ease-bounce
        ${visible ? "scale-100 opacity-100" : "scale-0 opacity-0"}
      `}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <svg
        className={`
          w-16 h-16 drop-shadow-lg
          ${filled ? "text-sunshine-500" : "text-neutral-300"}
          ${visible && filled ? "animate-star-pop" : ""}
        `}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    </div>
  );
}

/**
 * Sparkle particle effect
 */
function Sparkle({
  delay,
  style,
}: {
  delay: number;
  style: React.CSSProperties;
}) {
  return (
    <div
      className="absolute animate-sparkle pointer-events-none"
      style={{
        ...style,
        animationDelay: `${delay}ms`,
        animationDuration: "1.5s",
      }}
    >
      <svg className="w-6 h-6 text-sunshine-400" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
      </svg>
    </div>
  );
}

/**
 * Checkmark icon for correct answers
 */
function CheckmarkIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * Try again icon (refresh arrow) for incorrect answers
 */
function TryAgainIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 11-3.36-7.03" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

export default AnswerFeedback;
